<template>
    <div class="sensors-content">
        <div class="kpi-container">
            <KPICard type="issues" iconClass="exclamation" :label="$t('config.sensor.issues')" :value="`${kpiData.issues}/${sensors.length}`" :linkText="$t('config.sensor.filter_view')" :active="activeKpiType === 'issues'" @click="handleKpiClick" />
            <KPICard type="alerts" iconClass="menu-task" :label="$t('config.sensor.alerts')" :value="`${kpiData.alerts}/${sensors.length}`" :linkText="$t('config.sensor.filter_view')" :active="activeKpiType === 'alerts'" @click="handleKpiClick" />
            <KPICard type="offline" iconClass="bell-disabled" :label="$t('sensor.offline')" :value="`${kpiData.offline}/${sensors.length}`" :linkText="$t('config.sensor.filter_view')" :active="activeKpiType === 'offline'" @click="handleKpiClick" />
            <KPICard type="disconnected" iconClass="no-wifi" :label="$t('sensor.no_data')" :value="`${kpiData.disconnected}/${sensors.length}`" :linkText="$t('config.sensor.filter_view')" :active="activeKpiType === 'disconnected'" @click="handleKpiClick" />
            <KPICard type="connected" iconClass="menu-checklist" :label="$t('config.sensor.correct')" :value="`${kpiData.connected}/${sensors.length}`" :linkText="$t('config.sensor.filter_view')" :active="activeKpiType === 'connected'" @click="handleKpiClick" />
        </div>

        <div class="sensors-grid-container" v-if="isSensorsLoaded && sensors.length > 0  && conection">
            <div v-if="prioritySensors.length" class="sensors-grid">
                <SensorCard v-for="sensor in prioritySensors" :key="sensor.id" :sensor="sensor" @click.native="selectSensor(sensor)" />
            </div>
            <div v-else class="noConnectionBox">
                <EmptyPage :title="$t(activeKpiType === 'connected' ? 'sensor.empty_correct' : 'sensor.empty_alerts')" :description="$t(`sensor.${activeKpiType}_no_data_description`)"></EmptyPage>
            </div>
        </div>

        <div v-else-if="!conection" class="noConnectionBox">
            <ConnectionBox class="noConnection" gType="sensorConnection" @reload="load"></ConnectionBox>
        </div>

        <div v-else-if="isSensorsLoaded && sensors.length === 0" class="noConnectionBox">
            <ConnectionBox class="noConnection" gType="sensorConfiguration"></ConnectionBox>
        </div>
    </div>
</template>

<script>
import SensorCard from '@/components/domain/sensor/sensorCard.vue'
import KPICard from '@/components/domain/ui/KPICard.vue'
import ConnectionBox from '@/components/domain/ui/Box'
import EmptyPage from '@/components/layout/EmptyPage.vue'

export default {
    name: 'SensorsView',
    components: {
        SensorCard,
        KPICard,
        ConnectionBox,
        EmptyPage
    },
    props: {
        sensors: {
            type: Array,
            required: true
        },
        prioritySensors: {
            type: Array,
            required: true
        },
        kpiData: {
            type: Object,
            required: true
        },
        isSensorsLoaded: {
            type: Boolean,
            default: false
        },
        conection: {
            type: Boolean,
            default: true
        },
        activeKpiType: {
            type: String,
            default: null
        }
    },
    methods: {
        handleKpiClick(type) {
            this.$emit('kpi-click', type)
        },
        selectSensor(sensor) {
            this.$emit('sensor-select', sensor)
        },
        load() {
            let self = this
            this.$overlay.loading()
            let date = this.currentFilters
            let params = {
                start_date: date.type_date != 'custom' ? date.type_date : date.date.start_date,
                end_date: date.type_date == 'custom' ? date.date.end_date : false
            }
            this.conection = fnCheckConnection()
            this.$store.dispatch('sensor/loadList').then(() => {
                this.$store.dispatch('sensor/loadSensorValues', params).then(() => {
                    if (self.$route.params.id) {
                        self.sensorSelected = self.$store.getters['sensor/getSensorValues'](self.$route.params.id).id
                    }
                    self.$overlay.hide()
                    if (fnCheckConnection()) {
                        self.isSensorsLoaded = true
                    }
                })
            })
        }
    }
}
</script>

<style lang="scss" scoped>
.sensors-content {
    width: 100%;
    height: auto;
    flex: 1;
    min-height: 0;
    @include display-flex();
    @include flex-direction(column);
    overflow: hidden;

    .sensors-grid-container {
        @include display-flex();
        @include flex-direction(column);
        flex: 1;
        min-height: 0;
        overflow: hidden;
    }

    .kpi-container {
        @include display-flex();
        gap: 12px;
        margin: 16px 0;
        flex-wrap: nowrap;
        flex-shrink: 0;
        overflow-x: auto;
        overflow-y: hidden;
        -webkit-overflow-scrolling: touch;

        // Hide scrollbar but keep functionality
        scrollbar-width: none;
        -ms-overflow-style: none;

        &::-webkit-scrollbar {
            display: none;
        }

        .kpi-card {
            flex: 1 1 0%;
            min-width: 220px;
            flex-shrink: 1;
            width: auto;
        }
    }

    .sensors-grid {
        @include display-flex();
        @include flex-wrap(wrap);
        gap: 16px;
        align-content: flex-start;
        flex: 1;
        overflow-y: auto;
        overflow-x: hidden;
        padding-bottom: 16px;

        // Override sensor card widths to properly fill the grid
        ::v-deep .sensor-container {
            flex: 1 1 calc(20% - 16px);
            max-width: calc(20% - 13px);
            min-width: 200px;

            @media (max-width: 1800px) {
                flex: 1 1 calc(25% - 16px);
                max-width: calc(25% - 12px);
            }

            @media (max-width: 1400px) {
                flex: 1 1 calc(33.333% - 16px);
                max-width: calc(33.333% - 11px);
            }

            @media (max-width: 1000px) {
                flex: 1 1 calc(50% - 16px);
                max-width: calc(50% - 8px);
            }

            @media (max-width: 600px) {
                flex: 1 1 100%;
                max-width: 100%;
            }
        }
    }

    .noConnectionBox {
        @include display-flex();
        @include align-items(center);
        @include justify-content(center);
        flex: 1;
    }
}

// Mobile/Portrait responsive styles
@include media('<=1100px') {
    .sensors-content {
        .kpi-container {
            margin-bottom: 8px !important;
        }

        .sensors-grid {
            padding-bottom: 8px !important;
        }
    }
}
</style>
